import { FC, useState } from 'react';
import {
  ActionsMenu,
  Loader,
  NothingFoundAlert,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
  Typography,
} from 'melp-design/components';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import {
  EmployeeBenefitsAssignments,
  EmployeeBenefitsAssignmentsPreview,
  EmployeeSettings,
  EmployeeGroupsAssignments,
  EmployeeLogs,
} from 'containers/employees';
import { ROUTES } from 'config';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useRouteParams } from 'utils/useIdParam';
import { DeleteEmployeeDialog } from 'components/employees/DeleteEmployeeDialog/DeleteEmployeeDialog';
import { useMe } from 'state/Administrators';
import { useTabsState } from 'utils/Tabs';
import { useModalContext } from 'store/modal';
import { DateSelectModal } from 'containers/employees/atoms';
import {
  COLOR_BY_EMPLOYEE_STATUS,
  useBulkUpdateEmployees,
  useDeleteEmployee,
  useEmployee,
  useSendInvitations,
} from 'store/employees';
import { Stack } from '@mui/material';
import { getTimezoneFromDate } from 'utils/Common';
import { usePredefinedToasts } from 'utils/Toast';
import EmployeeWallet from '../employee-wallet/employee-wallet.component';

const employeeDetailsTabs = [
  'innerEmployeeEmployeeInformation',
  'benefitAssignmentsPreview',
  'innerEmployeeAssignBenefits',
  'innerEmployeeAssignGroups',
  'wallet',
  'innerEmployeeLog',
] as const;

const employeeDetailsTabsSchema = z.object({
  tab: z.enum(employeeDetailsTabs).catch(employeeDetailsTabs[0]),
});

export const EmployeeDetails: FC = () => {
  const { t } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);
  const history = useHistory();
  const { hasEditPermission, hasViewPermission } = useMe();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();

  const [openDeleteDialog, setDeleteDialog] = useState(false);

  const { data: employee, isLoading } = useEmployee(employeeId);

  const { mutate: bulkUpdateEmployees, isLoading: isBulkUpdating } =
    useBulkUpdateEmployees();
  const { mutate: sendInvitations, isLoading: isSendingInvitations } =
    useSendInvitations();
  const { mutate: deleteEmployee, isLoading: isDeleting } = useDeleteEmployee();

  const sideTabs = useTabsState(
    [
      {
        key: 'innerEmployeeEmployeeInformation',
        label: t('sidebar.administratorsInformation'),
      },
      ...(hasViewPermission(['benefits'])
        ? [
            {
              key: 'benefitAssignmentsPreview',
              label: t('menu.benefits'),
            } as const,
            {
              key: 'innerEmployeeAssignBenefits',
              label: t('sidebar.assignBenefits'),
            } as const,
          ]
        : []),
      ...(hasViewPermission(['groups'])
        ? [
            {
              key: 'innerEmployeeAssignGroups',
              label: t('sidebar.assignGroups'),
            } as const,
          ]
        : []),
      ...(hasViewPermission(['expenses'])
        ? [
            {
              key: 'wallet',
              label: t('sidebar.wallet'),
            } as const,
          ]
        : []),
      ...(hasViewPermission(['systemLog'])
        ? [
            {
              key: 'innerEmployeeLog',
              label: t('sidebar.log'),
            } as const,
          ]
        : []),
    ],
    employeeDetailsTabsSchema,
  );

  const handleCloseDeleteDialog = () => setDeleteDialog(false);

  const handleDeleteDeactivateSubmit = (value: string) => {
    if (value === 'delete') {
      deleteEmployee(
        { employeeId },
        {
          onSuccess: () => {
            predefinedToasts.success.deleted();
            history.push(ROUTES.employees.list);
          },
        },
      );
    } else if (value === 'deactivate') {
      const date = moment().toISOString();
      bulkUpdateEmployees(
        {
          employees: [employeeId],
          data: {
            endDate: date,
            endDateTZ: getTimezoneFromDate(date),
          },
        },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
          },
        },
      );
    }
    handleCloseDeleteDialog();
  };

  if (isLoading) return <Loader />;
  if (!employee) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('exportFileName.employees'),
            url: ROUTES.employees.list,
          },
          {
            label: `${employee.fullName} (${t('marketplace.employeeID', {
              id: employee.id,
            })})`,
          },
        ]}
        side={
          <ActionsMenu
            items={[
              ...(employee.status === 'active'
                ? [
                    {
                      label: t('actions.deactivate_now'),
                      disabled:
                        isBulkUpdating || !hasEditPermission(['employees']),
                      onClick: () => {
                        const date = moment().toISOString();
                        bulkUpdateEmployees(
                          {
                            employees: [employeeId],
                            data: {
                              endDate: date,
                              endDateTZ: getTimezoneFromDate(date),
                            },
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                    },
                    {
                      label: t('actions.deactivate_specific_date'),
                      disabled:
                        isBulkUpdating || !hasEditPermission(['employees']),
                      onClick: async () => {
                        const response = await openModal({
                          component: DateSelectModal,
                        });

                        if (response.action === 'CONFIRM') {
                          bulkUpdateEmployees(
                            {
                              employees: [employeeId],
                              data: {
                                endDate: response.date,
                                endDateTZ: getTimezoneFromDate(response.date),
                              },
                            },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.updated();
                              },
                            },
                          );
                        }
                      },
                    },
                  ]
                : []),
              ...(employee.status === 'inactive'
                ? [
                    {
                      label: t('actions.activate_now'),
                      disabled:
                        isBulkUpdating || !hasEditPermission(['employees']),
                      onClick: () => {
                        const date = moment().toISOString();
                        bulkUpdateEmployees(
                          {
                            employees: [employeeId],
                            data: {
                              startDate: date,
                              startDateTZ: getTimezoneFromDate(date),
                            },
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                    },
                    {
                      label: t('actions.activate_specific_date'),
                      disabled:
                        isBulkUpdating || !hasEditPermission(['employees']),
                      onClick: async () => {
                        const response = await openModal({
                          component: DateSelectModal,
                        });

                        if (response.action === 'CONFIRM') {
                          bulkUpdateEmployees(
                            {
                              employees: [employeeId],
                              data: {
                                startDate: response.date,
                                startDateTZ: getTimezoneFromDate(response.date),
                              },
                            },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.updated();
                              },
                            },
                          );
                        }
                      },
                    },
                  ]
                : []),
              {
                label: t('actions.invite_again'),
                disabled:
                  isSendingInvitations || !hasEditPermission(['employees']),
                onClick: () => {
                  sendInvitations(
                    {
                      employees: [employeeId],
                    },
                    {
                      onSuccess: () => {
                        toast(t('common.reminder_sent_successfully'), {
                          type: 'success',
                        });
                      },
                    },
                  );
                },
              },
              {
                label: t('actions.delete'),
                disabled: isDeleting || !hasEditPermission(['employees']),
                onClick: () => {
                  setDeleteDialog(true);
                },
                color: 'error',
              },
            ]}
          />
        }
        under={
          <Typography color={COLOR_BY_EMPLOYEE_STATUS[employee.status]}>
            {t(`status.${employee.status}`)}
          </Typography>
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            activeTab={sideTabs.activeTab}
            onTabChange={sideTabs.goTo}
            tabs={sideTabs.tabs}
          />
        }
      >
        {sideTabs.activeTab === 'innerEmployeeEmployeeInformation' && (
          <EmployeeSettings />
        )}
        {sideTabs.activeTab === 'benefitAssignmentsPreview' &&
          hasViewPermission(['benefits']) && (
            <EmployeeBenefitsAssignmentsPreview />
          )}
        {sideTabs.activeTab === 'innerEmployeeAssignBenefits' &&
          hasViewPermission(['benefits']) && <EmployeeBenefitsAssignments />}
        {sideTabs.activeTab === 'innerEmployeeAssignGroups' &&
          hasViewPermission(['groups']) && <EmployeeGroupsAssignments />}
        {sideTabs.activeTab === 'wallet' && hasViewPermission(['expenses']) && (
          <EmployeeWallet />
        )}
        {sideTabs.activeTab === 'innerEmployeeLog' &&
          hasViewPermission(['systemLog']) && <EmployeeLogs />}
      </PageContentLayout>

      <DeleteEmployeeDialog
        id="delete-deactivate-employee-dialog"
        open={openDeleteDialog}
        onCancelClick={handleCloseDeleteDialog}
        firstName={employee.firstName}
        onActionClick={handleDeleteDeactivateSubmit}
      />
    </Stack>
  );
};
